// drilldownAggregate — client-side aggregation for the Tableau Reports
// "Drilldown 2.0" tab. Reuses existing cached referrals / logins and
// synthesizes Parent1/2/3 by walking Salesforce `parentId` from each
// facility. CS Rep comes from `customerSuccessName` on the hierarchy
// node (falling back to the nearest ancestor that has one).
//
// Nothing in here talks to the network — the caller loads referrals /
// logins / hierarchy via existing `useCache` methods and hands them in.

import { unwrapDateLike } from './dateUtils';
import { recordDateYmd } from './userAnalytics';

const f = (r, k) => (r?.data || r?.Data || {})[k];

/** VE facility IDs land as floats ("2340.0") — canonicalize to "2340". */
export function canonicalVeId(raw) {
  if (raw == null || raw === '') return '';
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw).trim();
  return Math.round(n).toString();
}

// ─────────────────────────────────────────────────────────────────────
// Facility index
// ─────────────────────────────────────────────────────────────────────

/**
 * Flatten a hierarchy (roots + children) and return lookup helpers:
 *   - bySfId(id)  → node
 *   - byVeId(id)  → node
 *   - parents(id) → [parent1Name, parent2Name, parent3Name]  (immediate,
 *                    grandparent, great-grandparent — '' when the chain
 *                    is shorter)
 *   - csRep(id)   → customerSuccessName from the node or its ancestors
 *   - depth(id)   → 0-based depth from a hierarchy root
 *   - roots       → original root nodes (preserve SF tree order)
 *   - all()       → flat array of nodes
 */
export function buildFacilityIndex(hierarchy) {
  const bySfId = new Map();
  const byVeId = new Map();
  const depthBySfId = new Map();
  const roots = hierarchy || [];

  const walk = (node, depth) => {
    if (!node) return;
    const sfId = node.id || node.Id;
    if (sfId) {
      const key = String(sfId);
      bySfId.set(key, node);
      depthBySfId.set(key, depth);
      const ve = canonicalVeId(node.vitalEngine_Facility_ID__c ?? node.vitalEngineFacilityId);
      if (ve) byVeId.set(ve, node);
    }
    (node.children || node.Children || []).forEach(child => walk(child, depth + 1));
  };
  roots.forEach(n => walk(n, 0));

  const parents = (sfId) => {
    const out = ['', '', ''];
    if (!sfId) return out;
    let cur = bySfId.get(String(sfId));
    for (let level = 0; level < 3; level++) {
      const parentId = cur?.parentId || cur?.ParentId;
      const parent = parentId ? bySfId.get(String(parentId)) : null;
      if (!parent) break;
      out[level] = parent.name || parent.Name || '';
      cur = parent;
    }
    return out;
  };

  const csRep = (sfId) => {
    if (!sfId) return '';
    let cur = bySfId.get(String(sfId));
    while (cur) {
      const name = cur.customerSuccessName || cur.CustomerSuccessName;
      if (name && name.trim()) return name.trim();
      const parentId = cur.parentId || cur.ParentId;
      cur = parentId ? bySfId.get(String(parentId)) : null;
    }
    return '';
  };

  return {
    bySfId: (id) => bySfId.get(String(id ?? '')) || null,
    byVeId: (id) => byVeId.get(canonicalVeId(id)) || null,
    parents,
    csRep,
    depth: (id) => depthBySfId.get(String(id ?? '')) ?? 0,
    roots,
    all: () => [...bySfId.values()],
    size: bySfId.size,
    veIndexSize: byVeId.size,
  };
}

// ─────────────────────────────────────────────────────────────────────
// Period bucketing
// ─────────────────────────────────────────────────────────────────────

export const GRANULARITIES = ['year', 'quarter', 'month', 'week'];

function toDate(ymdOrDate) {
  if (!ymdOrDate) return null;
  if (ymdOrDate instanceof Date) {
    return Number.isNaN(ymdOrDate.getTime()) ? null : ymdOrDate;
  }
  const s = String(ymdOrDate);
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** ISO week number (1-53). */
function isoWeek(date) {
  const target = new Date(date.getTime());
  target.setHours(0, 0, 0, 0);
  target.setDate(target.getDate() + 3 - ((target.getDay() + 6) % 7));
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const week = 1 + Math.round(
    ((target - firstThursday) / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7
  );
  return { year: target.getFullYear(), week };
}

/** Return { key, label } for a date and granularity. */
export function periodBucket(ymd, granularity) {
  const d = toDate(ymd);
  if (!d) return null;
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  switch (granularity) {
    case 'year':
      return { key: `${year}`, label: `${year}` };
    case 'quarter': {
      const q = Math.floor((month - 1) / 3) + 1;
      return { key: `${year}-Q${q}`, label: `${year} Q${q}` };
    }
    case 'month':
      return {
        key: `${year}-${String(month).padStart(2, '0')}`,
        label: d.toLocaleString(undefined, { month: 'short', year: 'numeric' }),
      };
    case 'week': {
      const { year: wy, week } = isoWeek(d);
      const key = `${wy}-W${String(week).padStart(2, '0')}`;
      return { key, label: key };
    }
    default:
      return null;
  }
}

/** Enumerate all bucket keys/labels between two YMDs, inclusive. */
export function enumerateBuckets(startYmd, endYmd, granularity) {
  const start = toDate(startYmd);
  const end = toDate(endYmd);
  if (!start || !end || start > end) return [];
  const seen = new Map();
  const cursor = new Date(start.getTime());
  cursor.setHours(0, 0, 0, 0);
  // Step by day — cheap enough for a year window and keeps the code simple.
  while (cursor <= end) {
    const b = periodBucket(cursor, granularity);
    if (b && !seen.has(b.key)) seen.set(b.key, b);
    cursor.setDate(cursor.getDate() + 1);
  }
  return [...seen.values()];
}

// ─────────────────────────────────────────────────────────────────────
// Row extraction
// ─────────────────────────────────────────────────────────────────────

/** Pull the fields we care about off a referral row (canonicalized). */
export function extractReferral(row) {
  const day = recordDateYmd(row) || unwrapDateLike(f(row, 'created_date'));
  return {
    ymd: typeof day === 'string' ? day.slice(0, 10) : '',
    recvFacilityVeId: canonicalVeId(f(row, 'receiving_facility_id')),
    recvFacilityName: f(row, 'receiving_facility') || '',
    recvUserId: canonicalVeId(f(row, 'receiving_user_id')),
    recvUserName: f(row, 'receiving_user_name') || '',
    sendFacilityVeId: canonicalVeId(f(row, 'sending_facility_id')),
    sendFacilityName: f(row, 'sending_facility') || '',
    sendUserId: canonicalVeId(f(row, 'sending_user_id')),
    sendUserName: f(row, 'sending_user_name') || '',
    referralId: canonicalVeId(f(row, 'referral_id') || f(row, 'referral_number')),
  };
}

export function extractLogin(row) {
  return {
    ymd: recordDateYmd(row),
    facilityVeId: canonicalVeId(f(row, 'facility_id')),
    userId: canonicalVeId(f(row, 'user_id')),
    userName: f(row, 'user_display_name') || '',
    userType: f(row, 'user_type') || '',
  };
}

// ─────────────────────────────────────────────────────────────────────
// Row key = drilldown dimension
// ─────────────────────────────────────────────────────────────────────

export const DRILLDOWN_LEVELS = [
  { id: 'all', label: 'All' },
  { id: 'csRep', label: 'CS Rep' },
  { id: 'parent3', label: 'Parent 3' },
  { id: 'parent2', label: 'Parent 2' },
  { id: 'parent1', label: 'Parent 1' },
  { id: 'facility', label: 'Facility' },
];

/** For a facility VE id, produce { rowKey, rowLabel, sfId } for the current level. */
function rowKeyForFacility(veId, drilldown, index) {
  const node = index.byVeId(veId);
  const sfId = node ? (node.id || node.Id) : null;
  const facName = node ? (node.name || node.Name) : `Facility ${veId || '—'}`;
  switch (drilldown) {
    case 'all':
      return { rowKey: 'all', rowLabel: 'All', sfId };
    case 'csRep': {
      const cs = sfId ? index.csRep(sfId) : '';
      return { rowKey: cs || '(no CS)', rowLabel: cs || '(no CS)', sfId };
    }
    case 'parent1':
    case 'parent2':
    case 'parent3': {
      const [p1, p2, p3] = sfId ? index.parents(sfId) : ['', '', ''];
      const chosen = drilldown === 'parent1' ? p1 : drilldown === 'parent2' ? p2 : p3;
      return { rowKey: chosen || '(none)', rowLabel: chosen || '(none)', sfId };
    }
    case 'facility':
    default:
      return { rowKey: sfId || `ve:${veId || 'unknown'}`, rowLabel: facName, sfId };
  }
}

// ─────────────────────────────────────────────────────────────────────
// Aggregation — facility heatmap
// ─────────────────────────────────────────────────────────────────────

/**
 * Facility-level heatmap.
 *
 * When drilldown === 'facility', rows are ordered by the Salesforce
 * hierarchy (depth-first) with indentation depth, and ancestor nodes
 * that only appear as parents of active facilities are included as
 * rollup rows so the tree is readable.
 *
 * @returns {{ columns: Array<{key,label}>, rows: Array<{rowKey,rowLabel,total,buckets,facilityIds,depth?,isGroup?,hasOwnActivity?}> }}
 */
export function aggregateFacility({
  metric,
  granularity,
  drilldown,
  referrals = [],
  logins = [],
  index,
  startYmd,
  endYmd,
}) {
  const columns = enumerateBuckets(startYmd, endYmd, granularity);
  const rowMap = new Map(); // rowKey -> { rowKey, rowLabel, buckets: Map<colKey, count|Set> }

  const ensureRow = (rowKey, rowLabel, sfId) => {
    if (!rowMap.has(rowKey)) {
      rowMap.set(rowKey, {
        rowKey,
        rowLabel,
        buckets: new Map(),
        facilityIds: new Set(),
      });
    }
    const r = rowMap.get(rowKey);
    if (sfId) r.facilityIds.add(sfId);
    return r;
  };

  const bump = (row, colKey, uniqueKey) => {
    if (!row.buckets.has(colKey)) {
      row.buckets.set(colKey, uniqueKey ? new Set() : 0);
    }
    const cell = row.buckets.get(colKey);
    if (uniqueKey) cell.add(uniqueKey);
    else row.buckets.set(colKey, cell + 1);
  };

  if (metric === 'referrals-received') {
    for (const raw of referrals) {
      const r = extractReferral(raw);
      const bucket = periodBucket(r.ymd, granularity);
      if (!bucket) continue;
      const { rowKey, rowLabel, sfId } = rowKeyForFacility(r.recvFacilityVeId, drilldown, index);
      const row = ensureRow(rowKey, rowLabel, sfId);
      bump(row, bucket.key);
    }
  } else if (metric === 'logins-unique-users') {
    for (const raw of logins) {
      const l = extractLogin(raw);
      if (!l.userId) continue;
      const bucket = periodBucket(l.ymd, granularity);
      if (!bucket) continue;
      const { rowKey, rowLabel, sfId } = rowKeyForFacility(l.facilityVeId, drilldown, index);
      const row = ensureRow(rowKey, rowLabel, sfId);
      bump(row, bucket.key, l.userId);
    }
  }

  const finalizeRow = (r, extras = {}) => {
    const buckets = {};
    let total = 0;
    for (const [colKey, val] of r.buckets) {
      const n = val instanceof Set ? val.size : val;
      buckets[colKey] = n;
      total += n;
    }
    return {
      rowKey: r.rowKey,
      rowLabel: r.rowLabel,
      total,
      buckets,
      facilityIds: [...r.facilityIds],
      depth: 0,
      isGroup: false,
      hasOwnActivity: true,
      ...extras,
    };
  };

  if (drilldown === 'facility') {
    return {
      columns,
      rows: orderFacilityRowsByHierarchy(rowMap, columns, index, finalizeRow),
    };
  }

  const rows = [...rowMap.values()].map(r => finalizeRow(r));
  rows.sort((a, b) => b.total - a.total || String(a.rowLabel).localeCompare(String(b.rowLabel)));
  return { columns, rows };
}

/**
 * Walk the SF facility tree depth-first. Emit:
 *   - activity rows for facilities that appear in rowMap
 *   - ancestor group rows (rolled-up totals) so the hierarchy is visible
 * Facilities with activity but no SF match are appended at the end.
 *
 * Rollups union Sets (unique-user metrics) or sum numbers (referral counts)
 * so parent totals stay consistent with the metric grain.
 */
function orderFacilityRowsByHierarchy(rowMap, columns, index, finalizeRow) {
  const activeSfIds = new Set();
  for (const r of rowMap.values()) {
    if (r.rowKey && !String(r.rowKey).startsWith('ve:')) activeSfIds.add(String(r.rowKey));
  }

  const neededSfIds = new Set(activeSfIds);
  for (const sfId of activeSfIds) {
    let cur = index.bySfId(sfId);
    while (cur) {
      const parentId = cur.parentId || cur.ParentId;
      if (!parentId) break;
      const parentKey = String(parentId);
      if (!index.bySfId(parentKey)) break;
      neededSfIds.add(parentKey);
      cur = index.bySfId(parentKey);
    }
  }

  const ordered = [];
  const emitted = new Set();

  const emptyBuckets = (asSets) => {
    const m = new Map();
    for (const c of columns) m.set(c.key, asSets ? new Set() : 0);
    return m;
  };

  // Detect whether this aggregation used Sets (unique users) or counts.
  let usesSets = false;
  for (const r of rowMap.values()) {
    for (const v of r.buckets.values()) {
      if (v instanceof Set) { usesSets = true; break; }
    }
    if (usesSets) break;
  }

  const mergeBuckets = (into, from) => {
    for (const [k, v] of from) {
      if (usesSets) {
        const target = into.get(k) instanceof Set ? into.get(k) : new Set();
        if (v instanceof Set) v.forEach(x => target.add(x));
        else if (v != null && v !== 0) target.add(String(v));
        into.set(k, target);
      } else {
        const n = v instanceof Set ? v.size : (Number(v) || 0);
        into.set(k, (Number(into.get(k)) || 0) + n);
      }
    }
  };

  // Pre-order walk: emit parent, then children — so trees read like folders
  // (State of Alabama on top, nested health systems underneath).
  // Returns { rollup, rows } for the subtree.
  const walk = (node, depth, parentSfId) => {
    const sfId = String(node.id || node.Id || '');
    const children = node.children || node.Children || [];

    // Recurse first so we can roll up totals, but keep each child's rows
    // aside so we can place them *after* the parent in the output.
    const childWalks = children.map(ch => walk(ch, depth + 1, sfId || parentSfId));

    if (!sfId || !neededSfIds.has(sfId)) {
      const blank = emptyBuckets(usesSets);
      for (const cw of childWalks) mergeBuckets(blank, cw.rollup);
      // Ancestors of active facilities are always in neededSfIds, so a
      // skipped node cannot have needed descendants.
      return { rollup: blank, rows: [] };
    }

    const own = rowMap.get(sfId);
    const rollup = emptyBuckets(usesSets);
    if (own) mergeBuckets(rollup, own.buckets);
    for (const cw of childWalks) mergeBuckets(rollup, cw.rollup);

    const hasOwn = Boolean(own);
    const label = (node.name || node.Name || own?.rowLabel || sfId);
    const emittedChildCount = children.filter((ch) => {
      const cid = String(ch.id || ch.Id || '');
      return cid && neededSfIds.has(cid);
    }).length;

    const row = finalizeRow(
      {
        rowKey: sfId,
        rowLabel: label,
        buckets: rollup,
        facilityIds: own?.facilityIds || new Set([sfId]),
      },
      {
        depth,
        parentSfId: parentSfId || null,
        hasChildren: emittedChildCount > 0,
        isGroup: !hasOwn,
        hasOwnActivity: hasOwn,
      }
    );
    emitted.add(sfId);

    return {
      rollup,
      rows: [row, ...childWalks.flatMap(cw => cw.rows)],
    };
  };

  for (const root of (index.roots || [])) {
    const result = walk(root, 0, null);
    ordered.push(...result.rows);
  }

  for (const r of rowMap.values()) {
    if (emitted.has(r.rowKey)) continue;
    ordered.push(finalizeRow(r, {
      depth: 0,
      parentSfId: null,
      hasChildren: false,
      isGroup: false,
      hasOwnActivity: true,
    }));
  }

  return ordered;
}

// ─────────────────────────────────────────────────────────────────────
// Individual view — receiving users at a facility
// ─────────────────────────────────────────────────────────────────────

/**
 * For a selected facility (by SF id), roll referrals + logins up to
 * individual users × period.
 */
export function aggregateIndividual({
  metric,
  granularity,
  facilitySfId,
  referrals = [],
  logins = [],
  index,
  startYmd,
  endYmd,
}) {
  const columns = enumerateBuckets(startYmd, endYmd, granularity);
  const node = facilitySfId ? index.bySfId(facilitySfId) : null;
  const veId = node ? canonicalVeId(node.vitalEngine_Facility_ID__c ?? node.vitalEngineFacilityId) : '';
  const facLabel = node ? (node.name || node.Name || '') : '';

  // Precompute user_type from logins (v1: only source of user_type we have).
  const userTypeByUserId = new Map();
  const userNameByUserId = new Map();
  for (const raw of logins) {
    const l = extractLogin(raw);
    if (!l.userId) continue;
    if (l.userType && !userTypeByUserId.has(l.userId)) userTypeByUserId.set(l.userId, l.userType);
    if (l.userName && !userNameByUserId.has(l.userId)) userNameByUserId.set(l.userId, l.userName);
  }

  const rowMap = new Map();
  const ensureRow = (userId, userName) => {
    if (!rowMap.has(userId)) {
      rowMap.set(userId, {
        rowKey: userId,
        rowLabel: userName || userNameByUserId.get(userId) || `User ${userId}`,
        userType: userTypeByUserId.get(userId) || '',
        buckets: new Map(),
      });
    }
    return rowMap.get(userId);
  };

  const bump = (row, colKey, uniqueKey) => {
    if (!row.buckets.has(colKey)) {
      row.buckets.set(colKey, uniqueKey ? new Set() : 0);
    }
    const cell = row.buckets.get(colKey);
    if (uniqueKey) cell.add(uniqueKey);
    else row.buckets.set(colKey, cell + 1);
  };

  if (metric === 'referrals-received') {
    for (const raw of referrals) {
      const r = extractReferral(raw);
      if (r.recvFacilityVeId !== veId) continue;
      if (!r.recvUserId) continue;
      const bucket = periodBucket(r.ymd, granularity);
      if (!bucket) continue;
      const row = ensureRow(r.recvUserId, r.recvUserName);
      bump(row, bucket.key);
    }
  } else if (metric === 'logins-unique-users') {
    for (const raw of logins) {
      const l = extractLogin(raw);
      if (l.facilityVeId !== veId) continue;
      if (!l.userId) continue;
      const bucket = periodBucket(l.ymd, granularity);
      if (!bucket) continue;
      const row = ensureRow(l.userId, l.userName);
      // For unique-users on individual view, count login-days.
      bump(row, bucket.key, l.ymd);
    }
  }

  const rows = [...rowMap.values()].map(r => {
    const buckets = {};
    let total = 0;
    for (const [colKey, val] of r.buckets) {
      const n = val instanceof Set ? val.size : val;
      buckets[colKey] = n;
      total += n;
    }
    return { ...r, total, buckets };
  });

  rows.sort((a, b) => b.total - a.total || a.rowLabel.localeCompare(b.rowLabel));
  return { columns, rows, facility: { sfId: facilitySfId, veId, label: facLabel } };
}

// ─────────────────────────────────────────────────────────────────────
// Spoke drilldown — inbound referrals to a hub user, grouped by
// sending facility → sending user.
// ─────────────────────────────────────────────────────────────────────

export function aggregateSpoke({
  facilitySfId,
  hubUserId,
  referrals = [],
  index,
}) {
  const node = facilitySfId ? index.bySfId(facilitySfId) : null;
  const veId = node ? canonicalVeId(node.vitalEngine_Facility_ID__c ?? node.vitalEngineFacilityId) : '';

  const facilities = new Map(); // sendFacKey -> { label, total, users: Map }

  for (const raw of referrals) {
    const r = extractReferral(raw);
    if (r.recvFacilityVeId !== veId) continue;
    if (hubUserId && r.recvUserId !== canonicalVeId(hubUserId)) continue;

    const facKey = r.sendFacilityVeId || r.sendFacilityName || '(unknown)';
    if (!facilities.has(facKey)) {
      facilities.set(facKey, {
        rowKey: facKey,
        rowLabel: r.sendFacilityName || `Facility ${facKey}`,
        total: 0,
        users: new Map(),
      });
    }
    const bucket = facilities.get(facKey);
    bucket.total += 1;

    const userKey = r.sendUserId || r.sendUserName || '(unknown)';
    if (!bucket.users.has(userKey)) {
      bucket.users.set(userKey, {
        rowKey: userKey,
        rowLabel: r.sendUserName || `User ${userKey}`,
        total: 0,
      });
    }
    bucket.users.get(userKey).total += 1;
  }

  const rows = [...facilities.values()].map(f => ({
    ...f,
    users: [...f.users.values()].sort((a, b) => b.total - a.total || a.rowLabel.localeCompare(b.rowLabel)),
  }));
  rows.sort((a, b) => b.total - a.total || a.rowLabel.localeCompare(b.rowLabel));
  return { rows, facility: { sfId: facilitySfId, veId } };
}

// ─────────────────────────────────────────────────────────────────────
// Metrics catalog — surfaces the API gaps in-page.
// ─────────────────────────────────────────────────────────────────────

export const METRIC_OPTIONS = [
  { id: 'referrals-received', label: 'Referrals Received', shortLabel: 'Referrals', enabled: true,
    note: 'Cached referrals grouped by receiving_facility_id / receiving_user_id.' },
  { id: 'logins-unique-users', label: 'Logins (unique users)', shortLabel: 'Logins', enabled: true,
    note: 'Distinct user_id from the cached logins rollup per bucket.' },
  { id: 'chats', label: 'Chats', shortLabel: 'Chats', enabled: false,
    note: 'Needs rpt_chats-level events. Cache only has daily VChat message counters today.' },
  { id: 'vmails', label: 'VMails', shortLabel: 'VMails', enabled: false,
    note: 'Needs rpt_vmails-level events. Cache only has daily VMail message counters today.' },
  { id: 'telemed', label: 'Telemedicine Appointments', shortLabel: 'Telemed', enabled: false,
    note: 'Needs rpt_telemedicine_appointments in the cache. We only have telemedicine_login counts today.' },
  { id: 'fax', label: 'Faxes', shortLabel: 'Faxes', enabled: false, note: 'Not yet in the VE cache.' },
  { id: 'dicom', label: 'DICOM Studies (unique GUIDs)', shortLabel: 'DICOM', enabled: false,
    note: 'Needs rpt_referral.GUID on flat referrals for the COUNTD semantics Tableau uses.' },
];

/**
 * Fan out one-or-more single-metric heatmap results into a combined table.
 * When multiple metrics are selected, each entity (facility / user) becomes
 * N sibling rows — one per metric — labeled "Name · Referrals", "Name · Logins".
 * Hierarchy expand/collapse still keys off <c>entityKey</c> (the original
 * facility/user id) so collapsing a parent hides every metric variant of
 * its children.
 *
 * @param {Array<{ metricId: string, label: string, shortLabel?: string, columns: any[], rows: any[] }>} parts
 */
export function combineMetricHeatmaps(parts) {
  if (!parts || parts.length === 0) return { columns: [], rows: [] };
  const columns = parts[0].columns || [];
  const multi = parts.length > 1;

  if (!multi) {
    const p = parts[0];
    return {
      columns,
      rows: (p.rows || []).map(r => ({
        ...r,
        entityKey: String(r.rowKey),
        entityLabel: r.rowLabel,
        metricId: p.metricId,
        metricLabel: p.shortLabel || p.label,
      })),
    };
  }

  // Preserve hierarchy / sort order: first appearance of each entity wins.
  const order = [];
  const seen = new Set();
  const templates = new Map(); // entityKey -> first row seen (hierarchy fields)
  const byEntityMetric = new Map(); // `${entity}::${metricId}` -> row

  for (const part of parts) {
    for (const r of part.rows || []) {
      const entityKey = String(r.rowKey);
      if (!seen.has(entityKey)) {
        seen.add(entityKey);
        order.push(entityKey);
        templates.set(entityKey, r);
      }
      byEntityMetric.set(`${entityKey}::${part.metricId}`, r);
    }
  }

  const emptyBuckets = () => {
    const b = {};
    for (const c of columns) b[c.key] = 0;
    return b;
  };

  const rows = [];
  for (const entityKey of order) {
    const template = templates.get(entityKey);
    let firstVariant = true;
    for (const part of parts) {
      const short = part.shortLabel || part.label;
      const existing = byEntityMetric.get(`${entityKey}::${part.metricId}`);
      const base = existing || template;
      if (!base) continue;

      const buckets = existing
        ? existing.buckets
        : emptyBuckets();
      let total = 0;
      if (existing) total = existing.total;
      else {
        for (const c of columns) total += Number(buckets[c.key]) || 0;
      }

      rows.push({
        ...base,
        rowKey: `${entityKey}::${part.metricId}`,
        entityKey,
        entityLabel: base.rowLabel,
        rowLabel: `${base.rowLabel} · ${short}`,
        metricId: part.metricId,
        metricLabel: short,
        buckets,
        total,
        // Only the first metric variant keeps the expand chevron so the
        // hierarchy control appears once per facility, not once per metric.
        hasChildren: firstVariant ? Boolean(template?.hasChildren) : false,
        isGroup: existing ? Boolean(existing.isGroup) : true,
        hasOwnActivity: existing ? Boolean(existing.hasOwnActivity) : false,
      });
      firstVariant = false;
    }
  }

  return { columns, rows };
}